import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { updateDoc, doc } from "firebase/firestore";
import { fetchExchangeRate } from "../../utils/api";

const ExpenseTab = ({ trip, theme, onUpdateTrip, isDarkMode }) => {
  const [transactions, setTransactions] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(0.23);

  useEffect(() => {
    setTransactions(trip.expenses || []);
    // ดึงเรทเงินล่าสุด
    fetchExchangeRate().then(setExchangeRate);
  }, [trip.expenses]);

  // 🌟 1. ตั้งค่าหมวดหมู่เริ่มต้น
  const defaultCategories = [
    { id: "food", label: "อาหาร", icon: "🍔", color: "#e67e22" },
    { id: "travel", label: "เดินทาง", icon: "🚕", color: "#3498db" },
    { id: "shopping", label: "ช้อปปิ้ง", icon: "🛍️", color: "#e74c3c" },
    { id: "stay", label: "ที่พัก", icon: "🏨", color: "#9b59b6" },
    { id: "activity", label: "กิจกรรม", icon: "🎟️", color: "#1abc9c" },
    { id: "other", label: "อื่นๆ", icon: "📝", color: "#7f8c8d" },
  ];

  const [customCategories, setCustomCategories] = useState(
    trip.customExpenseCategories || []
  );
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");

  const allCategories = [...defaultCategories, ...customCategories];

  const initialFormState = {
    type: "expense",
    title: "",
    inputCost: "", // เปลี่ยนมาใช้ inputCost เพื่อเก็บยอดที่พิมพ์เข้ามา
    currency: "THB", // 🌟 เพิ่มสกุลเงิน
    splitCount: 1,
    date: trip.start || new Date().toISOString().split("T")[0],
    category: "food",
    note: "",
  };

  const [formData, setFormData] = useState(initialFormState);

  // คำนวณยอดแบบ Real-time
  const currentInputCost = parseFloat(formData.inputCost) || 0;
  const amountTHB =
    formData.currency === "JPY"
      ? currentInputCost * exchangeRate
      : currentInputCost;
  const splitCount = parseInt(formData.splitCount) || 1;
  const amountPerPerson = splitCount > 1 ? amountTHB / splitCount : amountTHB;

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      setIsAddingCategory(false);
      return;
    }
    const newCat = {
      id: `exp_cat_${Date.now()}`,
      label: newCategoryName.trim(),
      icon: "🏷️",
      color: "#2ed573",
    };
    const updatedCategories = [...customCategories, newCat];
    try {
      await updateDoc(doc(db, "trips", trip.id), {
        customExpenseCategories: updatedCategories,
      });
      trip.customExpenseCategories = updatedCategories;
      setCustomCategories(updatedCategories);
      setFormData({ ...formData, category: newCat.id });
      setNewCategoryName("");
      setIsAddingCategory(false);
    } catch (error) {
      console.error("Error saving custom category:", error);
      alert("เกิดข้อผิดพลาดในการบันทึกหมวดหมู่");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.inputCost)
      return alert("กรุณากรอกชื่อรายการและจำนวนเงิน");

    const newTx = {
      ...formData,
      id: editingId || `tx_${Date.now()}`,
      inputCost: parseFloat(formData.inputCost),
      amount: amountTHB, // ยอดสรุปเก็บเป็น THB เสมอ
      splitCount: parseInt(formData.splitCount) || 1,
      amountPerPerson: amountPerPerson,
    };

    let newList;
    if (editingId) {
      newList = transactions.map((t) => (t.id === editingId ? newTx : t));
    } else {
      newList = [newTx, ...transactions];
    }

    setTransactions(newList);
    onUpdateTrip(newList, "expenses");
    setIsAdding(false);
    setEditingId(null);
    setFormData(initialFormState);

    try {
      await updateDoc(doc(db, "trips", trip.id), { expenses: newList });
    } catch (error) {
      console.error("Error saving expense:", error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("คุณต้องการลบรายการนี้ใช่หรือไม่?")) {
      const newList = transactions.filter((t) => t.id !== id);
      setTransactions(newList);
      onUpdateTrip(newList, "expenses");

      try {
        await updateDoc(doc(db, "trips", trip.id), { expenses: newList });
      } catch (error) {
        console.error("Error deleting expense:", error);
      }
    }
  };

  const handleEditClick = (item) => {
    setFormData({
      ...initialFormState,
      ...item,
      inputCost: item.inputCost !== undefined ? item.inputCost : item.amount,
      currency: item.currency || "THB",
    });
    setEditingId(item.id);
    setIsAdding(false); // ปิดฟอร์มด้านบน
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData(initialFormState);
  };

  // --- คำนวณสรุปยอด (Summary) ในหน่วยบาท (THB) ---
  const totalIncome = transactions
    .filter((t) => t.type === "income")
    .reduce((sum, t) => sum + (t.amount || 0), 0);

  const myTotalExpense = transactions
    .filter((t) => t.type === "expense")
    .reduce((sum, t) => sum + (t.amountPerPerson || t.amount || 0), 0);

  const balance = totalIncome - myTotalExpense;

  const inputStyle = {
    padding: "10px 12px",
    borderRadius: "8px",
    border: `1px solid ${isDarkMode ? "#444" : "#ddd"}`,
    backgroundColor: isDarkMode ? "#2c2c2c" : "#fff",
    color: isDarkMode ? "#fff" : "#000",
    fontSize: "14px",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  };

  const labelStyle = {
    fontSize: "12px",
    fontWeight: "bold",
    color: isDarkMode ? "#aaa" : "#666",
    marginBottom: "5px",
    display: "block",
  };

  const cardStyle = {
    backgroundColor: theme?.cardBg || (isDarkMode ? "#1e1e1e" : "#fff"),
    border: `1px solid ${theme?.border || (isDarkMode ? "#333" : "#eee")}`,
    borderRadius: "12px",
    padding: "15px",
    marginBottom: "10px",
  };

  // 🌟 Component สำหรับฟอร์ม (ใช้ได้ทั้งเพิ่มใหม่ และ Edit Inline)
  const renderForm = () => (
    <div
      style={{
        ...cardStyle,
        border: "2px solid #3b82f6",
        marginBottom: "20px",
      }}
    >
      <div style={{ display: "flex", gap: "10px", marginBottom: "15px" }}>
        <button
          type="button"
          onClick={() => setFormData({ ...formData, type: "expense" })}
          style={{
            flex: 1,
            padding: "10px",
            borderRadius: "8px",
            border: "none",
            fontWeight: "bold",
            cursor: "pointer",
            background:
              formData.type === "expense"
                ? "#ff4757"
                : isDarkMode
                ? "#333"
                : "#eee",
            color:
              formData.type === "expense"
                ? "#fff"
                : isDarkMode
                ? "#aaa"
                : "#666",
          }}
        >
          💸 รายจ่าย
        </button>
        <button
          type="button"
          onClick={() => setFormData({ ...formData, type: "income" })}
          style={{
            flex: 1,
            padding: "10px",
            borderRadius: "8px",
            border: "none",
            fontWeight: "bold",
            cursor: "pointer",
            background:
              formData.type === "income"
                ? "#2ed573"
                : isDarkMode
                ? "#333"
                : "#eee",
            color:
              formData.type === "income"
                ? "#fff"
                : isDarkMode
                ? "#aaa"
                : "#666",
          }}
        >
          💰 รายรับ
        </button>
      </div>

      <form
        onSubmit={handleSave}
        style={{ display: "flex", flexDirection: "column", gap: "15px" }}
      >
        <div style={{ display: "flex", gap: "10px" }}>
          <div style={{ flex: 2 }}>
            <label style={labelStyle}>ชื่อรายการ</label>
            <input
              type="text"
              placeholder="เช่น ค่าอาหารเย็น, ค่าตั๋วรถไฟ"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              style={{ ...inputStyle, fontWeight: "bold" }}
              required
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>วันที่</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) =>
                setFormData({ ...formData, date: e.target.value })
              }
              style={inputStyle}
              required
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
          <div style={{ flex: 2 }}>
            <label style={labelStyle}>จำนวนเงิน</label>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <input
                type="number"
                step="any"
                placeholder="0"
                value={formData.inputCost}
                onChange={(e) =>
                  setFormData({ ...formData, inputCost: e.target.value })
                }
                style={{
                  ...inputStyle,
                  color: formData.type === "expense" ? "#ff4757" : "#2ed573",
                  fontWeight: "bold",
                  fontSize: "16px",
                  flex: 2,
                }}
                required
              />
              <select
                value={formData.currency}
                onChange={(e) =>
                  setFormData({ ...formData, currency: e.target.value })
                }
                style={{ ...inputStyle, flex: 1, padding: "10px" }}
              >
                <option value="THB">🇹🇭 THB</option>
                <option value="JPY">🇯🇵 JPY</option>
              </select>
            </div>
            {formData.currency === "JPY" && currentInputCost > 0 && (
              <div
                style={{
                  fontSize: "12px",
                  color: "#888",
                  marginTop: "6px",
                }}
              >
                แปลงเป็นเงินไทย ≈ ฿
                {amountTHB.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                })}
              </div>
            )}
          </div>

          {formData.type === "expense" && (
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>หารกี่คน?</label>
              <input
                type="number"
                min="1"
                value={formData.splitCount}
                onChange={(e) =>
                  setFormData({ ...formData, splitCount: e.target.value })
                }
                style={{ ...inputStyle, textAlign: "center" }}
              />
              {splitCount > 1 && amountTHB > 0 && (
                <div
                  style={{
                    fontSize: "12px",
                    color: "#e67e22",
                    marginTop: "6px",
                    fontWeight: "bold",
                  }}
                >
                  👉 คนละ ฿
                  {amountPerPerson.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          <label style={labelStyle}>หมวดหมู่</label>
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
            {allCategories.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setFormData({ ...formData, category: c.id })}
                style={{
                  padding: "8px 12px",
                  borderRadius: "20px",
                  border:
                    formData.category === c.id
                      ? `1px solid ${c.color || "#3b82f6"}`
                      : `1px solid ${isDarkMode ? "#444" : "#ddd"}`,
                  background:
                    formData.category === c.id
                      ? `${c.color || "#3b82f6"}20`
                      : "transparent",
                  color:
                    formData.category === c.id
                      ? c.color || "#3b82f6"
                      : isDarkMode
                      ? "#aaa"
                      : "#666",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: "bold",
                }}
              >
                {c.icon} {c.label}
              </button>
            ))}

            {isAddingCategory ? (
              <div style={{ display: "flex", gap: "4px" }}>
                <input
                  type="text"
                  placeholder="ชื่อหมวดหมู่..."
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddCategory();
                    }
                  }}
                  autoFocus
                  style={{
                    ...inputStyle,
                    padding: "6px 10px",
                    fontSize: "12px",
                    width: "120px",
                    borderRadius: "20px",
                  }}
                />
                <button
                  type="button"
                  onClick={handleAddCategory}
                  style={{
                    padding: "6px 12px",
                    borderRadius: "20px",
                    border: "none",
                    background: "#10b981",
                    color: "#fff",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: "600",
                  }}
                >
                  ✓
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsAddingCategory(false);
                    setNewCategoryName("");
                  }}
                  style={{
                    padding: "6px 12px",
                    borderRadius: "20px",
                    border: `1px solid ${isDarkMode ? "#444" : "#ddd"}`,
                    background: "transparent",
                    color: isDarkMode ? "#aaa" : "#666",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: "600",
                  }}
                >
                  ✕
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setIsAddingCategory(true)}
                style={{
                  padding: "8px 12px",
                  borderRadius: "20px",
                  border: `1px dashed ${isDarkMode ? "#555" : "#bbb"}`,
                  backgroundColor: "transparent",
                  color: isDarkMode ? "#aaa" : "#666",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: "600",
                }}
              >
                + เพิ่ม
              </button>
            )}
          </div>
        </div>

        <div>
          <label style={labelStyle}>บันทึกเพิ่มเติม (ใครจ่าย, โอนให้ใคร)</label>
          <input
            type="text"
            placeholder="เช่น A จ่ายไปก่อนเดี๋ยวโอนคืน"
            value={formData.note}
            onChange={(e) => setFormData({ ...formData, note: e.target.value })}
            style={inputStyle}
          />
        </div>

        <div
          style={{
            display: "flex",
            gap: "10px",
            justifyContent: "flex-end",
            marginTop: "10px",
          }}
        >
          {editingId && (
            <button
              type="button"
              onClick={() => handleDelete(editingId)}
              style={{
                padding: "8px 16px",
                borderRadius: "8px",
                border: "none",
                background: "rgba(239, 68, 68, 0.1)",
                color: "#ef4444",
                cursor: "pointer",
                fontWeight: "bold",
                marginRight: "auto",
              }}
            >
              🗑️ ลบ
            </button>
          )}
          <button
            type="button"
            onClick={handleCancel}
            style={{
              padding: "8px 16px",
              borderRadius: "8px",
              border: `1px solid ${isDarkMode ? "#555" : "#ccc"}`,
              background: "transparent",
              color: isDarkMode ? "#fff" : "#000",
              cursor: "pointer",
            }}
          >
            ยกเลิก
          </button>
          <button
            type="submit"
            style={{
              padding: "8px 24px",
              borderRadius: "8px",
              border: "none",
              background: "#3b82f6",
              color: "#fff",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            {editingId ? "💾 บันทึก" : "＋ บันทึก"}
          </button>
        </div>
      </form>
    </div>
  );

  return (
    <div style={{ maxWidth: "800px", margin: "0 auto", paddingBottom: "30px" }}>
      {/* 🌟 1. ส่วนสรุปยอด (Summary Board) 🌟 */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: "10px",
          marginBottom: "20px",
        }}
      >
        <div style={{ ...cardStyle, textAlign: "center" }}>
          <div style={{ fontSize: "12px", color: "#888", fontWeight: "bold" }}>
            รายรับ / กองกลาง
          </div>
          <div
            style={{
              fontSize: "20px",
              fontWeight: "bold",
              color: "#2ed573",
              marginTop: "5px",
            }}
          >
            +฿
            {totalIncome.toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}
          </div>
        </div>
        <div style={{ ...cardStyle, textAlign: "center" }}>
          <div style={{ fontSize: "12px", color: "#888", fontWeight: "bold" }}>
            รายจ่าย (ส่วนของเรา)
          </div>
          <div
            style={{
              fontSize: "20px",
              fontWeight: "bold",
              color: "#ff4757",
              marginTop: "5px",
            }}
          >
            -฿
            {myTotalExpense.toLocaleString(undefined, {
              minimumFractionDigits: 2,
            })}
          </div>
        </div>
        <div
          style={{
            ...cardStyle,
            textAlign: "center",
            border: `2px solid ${balance >= 0 ? "#2ed573" : "#ff4757"}`,
          }}
        >
          <div style={{ fontSize: "12px", color: "#888", fontWeight: "bold" }}>
            คงเหลือ
          </div>
          <div
            style={{
              fontSize: "20px",
              fontWeight: "bold",
              color: balance >= 0 ? "#2ed573" : "#ff4757",
              marginTop: "5px",
            }}
          >
            ฿{balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}
          </div>
        </div>
      </div>

      {/* 🌟 2. ปุ่มเพิ่มรายการ 🌟 */}
      {!isAdding && !editingId && (
        <button
          onClick={() => {
            setFormData(initialFormState);
            setEditingId(null);
            setIsAdding(true);
          }}
          style={{
            width: "100%",
            padding: "15px",
            borderRadius: "12px",
            border: `2px dashed ${isDarkMode ? "#555" : "#bbb"}`,
            background: "transparent",
            color: isDarkMode ? "#aaa" : "#666",
            fontSize: "15px",
            fontWeight: "bold",
            cursor: "pointer",
            marginBottom: "20px",
            transition: "0.2s",
          }}
        >
          ➕ เพิ่มรายการรับ-จ่าย
        </button>
      )}

      {/* ถ้ากด "เพิ่ม" ตรงๆ จากปุ่มด้านบน ให้แสดงฟอร์มที่ด้านบน */}
      {isAdding && !editingId && renderForm()}

      {/* 🌟 3. รายการที่บันทึกไว้ (ประวัติ) 🌟 */}
      <div>
        <h4 style={{ marginBottom: "15px" }}>🧾 ประวัติรายการ</h4>
        {transactions.length === 0 ? (
          <div style={{ textAlign: "center", color: "#888", padding: "20px" }}>
            ยังไม่มีรายการบันทึก
          </div>
        ) : (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "10px" }}
          >
            {transactions.map((t) => {
              // 🌟 ถ้ารายการนี้กำลังถูกกดแก้ไข (Inline Edit) ให้เรนเดอร์ฟอร์มแทน 🌟
              if (editingId === t.id) {
                return <div key={t.id}>{renderForm()}</div>;
              }

              // ค้นหา icon และ color จาก allCategories
              const categoryData = allCategories.find(
                (c) => c.id === t.category
              ) || { icon: "📝", color: "#7f8c8d" };

              const isExpense = t.type === "expense";
              return (
                <div
                  key={t.id}
                  style={{
                    ...cardStyle,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 15px",
                    borderLeft: `4px solid ${
                      isExpense ? "#ff4757" : "#2ed573"
                    }`,
                    position: "relative", // 🌟 กำหนด relative ให้การ์ด เพื่อวางจุดไข่ปลา
                  }}
                >
                  {/* 🌟 ปุ่มแก้ไข (จุด 3 จุด) ไว้มุมขวาบน 🌟 */}
                  <button
                    onClick={() => handleEditClick(t)}
                    style={{
                      position: "absolute",
                      top: "10px",
                      right: "12px",
                      background: "none",
                      border: "none",
                      color: "#888",
                      fontSize: "18px",
                      cursor: "pointer",
                      padding: "5px",
                      lineHeight: 1,
                    }}
                  >
                    •••
                  </button>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "15px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "20px",
                        backgroundColor: `${categoryData.color}20`,
                        padding: "8px",
                        borderRadius: "50%",
                        width: "35px",
                        height: "35px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {categoryData.icon}
                    </div>
                    <div>
                      <div
                        style={{
                          fontWeight: "bold",
                          fontSize: "15px",
                          color: theme?.text,
                        }}
                      >
                        {t.title}
                      </div>
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#888",
                          display: "flex",
                          gap: "8px",
                          marginTop: "2px",
                        }}
                      >
                        <span>{t.date}</span>
                        {t.note && <span>• {t.note}</span>}
                      </div>
                    </div>
                  </div>

                  <div style={{ textAlign: "right", paddingRight: "25px" }}>
                    <div
                      style={{
                        fontWeight: "bold",
                        fontSize: "16px",
                        color: isExpense ? "#ff4757" : "#2ed573",
                      }}
                    >
                      {isExpense ? "-" : "+"}
                      {t.currency === "JPY" ? "¥" : "฿"}
                      {(t.inputCost || t.amount).toLocaleString(undefined, {
                        minimumFractionDigits: t.currency === "JPY" ? 0 : 2,
                      })}
                    </div>

                    {/* แสดงเงินบาทที่แปลงแล้ว กรณีเป็น JPY */}
                    {t.currency === "JPY" && (
                      <div style={{ fontSize: "11px", color: "#888" }}>
                        ≈ ฿
                        {t.amount?.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </div>
                    )}

                    {/* แสดงยอดหาร ถ้ามี */}
                    {isExpense && t.splitCount > 1 && (
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#e67e22",
                          fontWeight: "bold",
                          marginTop: "2px",
                        }}
                      >
                        หาร {t.splitCount} = ฿
                        {t.amountPerPerson?.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpenseTab;
